import { Box, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SelectInput, useListContext, required, useDataProvider, useNotify, usePermissions, useTranslate } from 'react-admin';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import { Link } from 'react-router-dom';
import { ProductProgressBar } from '@/components/analytics/product/ProductAnalyticsInfo.jsx';
import PropTypes from 'prop-types';
import { serviceRankingTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import CircularProgress from '@mui/material/CircularProgress';
import { calcDaysFromSeriesData } from '@/components/analytics/PeriodFilters.jsx';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { buildListFilterUrl } from '@/helpers/filters/buildListFilterUrl.js';

const Title = () => {
    const translate = useTranslate();
    return <Typography variant='h6'>{translate('analytics.service.requestsCount')}</Typography>;
};

const StyledElement = styled(Box)(() => ({
    ...analyticsStyles.element,
}));

const Element = ({ item, type, total, days }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const percentage = (item.current / total) * 100;
    const growth = ((item.current - item.previous) / item.previous) * 100;
    const daysStr = days === 1 ? translate('analytics.common.day') : `${days} ${translate('analytics.common.days')}`;
    const servicesFilter = { search: { value: item.name }, status_in: ['closed'] };
    const servicesLink = buildListFilterUrl('/service-request', servicesFilter, ['search', 'status_in']);
    const provinceFilter = { 'collectionPoint->address->province_in': [item.id], status_in: ['closed'] };
    const provinceLink = buildListFilterUrl('/service-request', provinceFilter, ['collectionPoint->address->province_in', 'status_in']);
    const contractorsFilter = { 'collectionPoint->contractorId_in': [item.id], status_in: ['closed'] };
    const contractorsLink = buildListFilterUrl('/service-request', contractorsFilter, ['collectionPoint->contractorId_in', 'status_in']);

    const hasAccess = permissions && (permissions.isAdmin || permissions.isBok || permissions.isServiceMaster);

    let linkUrl = null;
    if (type === 'provinces') {
        linkUrl = provinceLink;
    } else if (type === 'contractors') {
        linkUrl = contractorsLink;
    }
    const shouldShowLink = hasAccess && !!linkUrl;

    return (
        <StyledElement className='element'>
            <Box className='element-header'>
                <Box className='element-left'>
                    {shouldShowLink ? (
                        <Link style={{ color: Palette.black, textDecoration: 'none' }} to={linkUrl}>
                            <Typography variant='caption'>{item.name}</Typography>
                        </Link>
                    ) : (
                        <Typography variant='caption'>{item.name}</Typography>
                    )}
                </Box>
                <Box className='element-right'>
                    <Tooltip
                        arrow
                        placement='top'
                        classes={{ tooltip: 'tooltip-white tooltip-white--small' }}
                        title={
                            <span>
                                {translate('analytics.service.closedCountLabel')} <br />
                                <strong>{daysStr}</strong>
                            </span>
                        }
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -15],
                                    },
                                },
                            ],
                        }}
                    >
                        <Link style={{ color: Palette.black, textDecoration: 'none' }} to={servicesLink}>
                            <Typography variant='caption'>{item.current}</Typography>
                        </Link>
                        <Growth value={growth} className='growth--small' />
                    </Tooltip>
                </Box>
                {isFinite(percentage) ? (
                    <Typography variant='caption' className='percentage'>
                        {formatNumberWithComma(percentage, 1)}%
                    </Typography>
                ) : null}
            </Box>
            <ProductProgressBar percentage={percentage} />
        </StyledElement>
    );
};

const StyledRanking = styled(Box)(() => ({
    padding: '40px 40px 48px',
    borderRadius: '16px',
    backgroundColor: Palette.white,
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .ranking-content': {
        marginTop: '36px',
    },
    '& .ranking-footer': {
        ...analyticsStyles.rankingFooter,
    },
    '& .element + .element': {
        marginTop: '17px',
    },
}));

export const ServiceRanking = () => {
    const translate = useTranslate();
    const defaultRankingFilters = { rankingType: 'provinces' };
    const { permissions } = usePermissions();
    const { data, filterValues } = useListContext();
    const form = useForm({ defaultValues: defaultRankingFilters });
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { watch } = form;
    const rankingFilterValues = watch();
    const filter = { ...filterValues, ...rankingFilterValues };

    const {
        data: rankingData,
        isFetching,
        error,
    } = useQuery({
        queryKey: ['service-ranking', { filter }],
        queryFn: () => dataProvider.summary('analytic-service/service-ranking', { filter }),
    });

    if (!data || data.length === 0) return null;

    if (error) {
        notify(error.message, { type: 'error' });
        return null;
    }

    const analytic = data.find(s => s.id === 'current').data;
    const series = data.find(s => s.id === 'series').data;
    const days = calcDaysFromSeriesData(series);
    const ranking = rankingData?.data?.data ?? [];

    return (
        <StyledRanking>
            <Box className='ranking-header ranking-header--inline'>
                <Title />
                {permissions && (permissions.isAdmin || permissions.isBok || permissions.isServiceMaster) && (
                    <Box className='ranking-header__filters'>
                        <FormProvider {...form}>
                            <SelectInput
                                source='rankingType'
                                choices={serviceRankingTypeChoices(translate)}
                                fullWidth
                                label={false}
                                validate={required()}
                                emptyText={translate('common.all')}
                            />
                        </FormProvider>
                    </Box>
                )}
            </Box>
            <Box className='ranking-content'>
                {isFetching ? (
                    <CircularProgress />
                ) : (
                    ranking.map(item => (
                        <Element key={item.id} item={item} type={rankingFilterValues.rankingType} total={analytic.serviceRequestCreatedCount} days={days} />
                    ))
                )}
            </Box>
        </StyledRanking>
    );
};

Element.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        categoryId: PropTypes.string.isRequired,
        categoryName: PropTypes.string.isRequired,
        previous: PropTypes.number.isRequired,
        current: PropTypes.number.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
    days: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
};
